import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import '../../modalright.css';
import '../../popup.css';
import '../../pricing-strategies.css';
import '../../wallet.css';
import { getDriverApiBaseUrl } from '../../utils/utils';
import {
    formatMoney, formatInt, formatDateTime, timeAgo,
    Pill, StatCard, EmptyState, WalletSpinner, IconButton,
    useWalletToasts, WalletToastStack,
} from './walletShared';

// Dedicated ops panel to attach "Duties & Tax" lines to a shipment AWB.
// Driver backend (5566); the driver app reads the same data on its collect-cash screen.
export default function DutiesTax() {
    const API_BASE = `${getDriverApiBaseUrl()}/shypvdriverapp/`;
    const email = sessionStorage.getItem('useremail') || '';
    const authHeaders = () => {
        const t = sessionStorage.getItem('usertoken');
        return t ? { Authorization: `Bearer ${t}` } : {};
    };
    const dutiesUrl = (path) => `${API_BASE}duties-tax/${path}`;
    const { toasts, toast, dismiss } = useWalletToasts();

    const [awb, setAwb] = useState('');
    const [loadedAwb, setLoadedAwb] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    // Add-duty module: one AWB, add several charges into a draft, then submit all at once
    const [asgAwb, setAsgAwb] = useState('');
    const [line, setLine] = useState({ name: '', amount: '', currency: 'AED', status: 'NOT_COLLECTED' });
    const [draft, setDraft] = useState([]);            // pending charges (not yet saved)
    const [draftKey, setDraftKey] = useState(1);
    const [assigning, setAssigning] = useState(false);

    // Excel import modal
    const [impOpen, setImpOpen] = useState(false);
    const [impRows, setImpRows] = useState([]);   // [{name, amount, currency}]
    const [impFileName, setImpFileName] = useState('');
    const [importing, setImporting] = useState(false);

    // Multi-AWB customs-report import (one row per AWB, split duty columns)
    const [multiOpen, setMultiOpen] = useState(false);
    const [multiFileName, setMultiFileName] = useState('');
    const [multiGroups, setMultiGroups] = useState([]);   // [{awb, matched, jobId, lines:[{key,name,amount,currency}]}]
    const [matching, setMatching] = useState(false);
    const [savingMulti, setSavingMulti] = useState(false);

    // Recently added this session (shown immediately) + always-visible upload log
    const [justAdded, setJustAdded] = useState([]);
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    // ── Load lines for an AWB ──────────────────────────────────────────────────
    const loadDuties = async (awbToLoad) => {
        const target = (awbToLoad ?? awb).trim();
        if (!target) return toast.error('Enter an AWB first.');
        setLoading(true);
        try {
            const res = await axios.get(dutiesUrl('byAwb'), { params: { awb: target }, headers: authHeaders() });
            setItems((res.data && res.data.data) || []);
            setLoadedAwb(target);
        } catch (err) {
            console.error(err);
            toast.error((err.response && err.response.data && err.response.data.errors
                && err.response.data.errors.map(e => e.errorMessage || e).join(', ')) || 'Failed to load duties & tax.');
            setItems([]);
        } finally { setLoading(false); }
    };

    const markCollected = async (id) => {
        try {
            await axios.put(dutiesUrl('collect'), null, { params: { id }, headers: authHeaders() });
            toast.success('Marked collected.');
            await loadDuties(loadedAwb);
        } catch (err) { console.error(err); toast.error('Failed to update.'); }
    };

    const deleteDuty = async (id) => {
        if (!window.confirm('Remove this line?')) return;
        try {
            await axios.delete(dutiesUrl('delete'), { params: { id }, headers: authHeaders() });
            toast.success('Removed.');
            await loadDuties(loadedAwb);
        } catch (err) { console.error(err); toast.error('Failed to delete.'); }
    };

    // ── Excel import ───────────────────────────────────────────────────────────
    const pick = (row, keys) => {
        for (const k of Object.keys(row)) {
            const norm = String(k).trim().toLowerCase();
            if (keys.includes(norm)) return row[k];
        }
        return undefined;
    };
    const onFile = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setImpFileName(file.name);
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const wb = XLSX.read(ev.target.result, { type: 'array' });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                const rows = json.map(r => ({
                    name: String(pick(r, ['name', 'duty', 'duties', 'description', 'item']) ?? '').trim(),
                    amount: pick(r, ['amount', 'amt', 'value', 'price']),
                    currency: String(pick(r, ['currency', 'ccy', 'cur']) ?? 'AED').trim() || 'AED',
                })).filter(r => r.name || r.amount !== '');
                setImpRows(rows);
                if (rows.length === 0) toast.error('No rows found. Expected columns: Name, Amount, Currency.');
            } catch (err) {
                console.error(err);
                toast.error('Could not read that file. Use .xlsx, .xls or .csv.');
            }
        };
        reader.readAsArrayBuffer(file);
    };
    const openImport = () => {
        if (!(loadedAwb || awb).trim()) return toast.error('Enter an AWB first, then import.');
        setImpRows([]); setImpFileName(''); setImpOpen(true);
    };
    const validImpRows = useMemo(
        () => impRows.filter(r => r.name && r.amount !== '' && !isNaN(Number(r.amount))),
        [impRows]
    );
    const submitImport = async () => {
        const target = (loadedAwb || awb).trim();
        if (validImpRows.length === 0) return toast.error('No valid rows to import.');
        setImporting(true);
        try {
            const res = await axios.post(dutiesUrl('bulk'),
                {
                    awb: target, uploadedByEmail: email,
                    items: validImpRows.map(r => ({ name: r.name, amount: Number(r.amount), currency: r.currency || 'AED' })),
                },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
            if (res.data && res.data.success) {
                const n = (res.data.data || []).length;
                toast.success(`Imported ${n} line(s) from ${impFileName || 'file'}.`);
                setImpOpen(false);
                setJustAdded(res.data.data || []);
                loadLogs();
                await loadDuties(target);
            } else {
                toast.error((res.data && res.data.errors && res.data.errors.map(e => e.errorMessage || e).join(', ')) || 'Import failed.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Import failed.');
        } finally { setImporting(false); }
    };
    // ── Multi-AWB customs-report import ─────────────────────────────────────────
    // Each spreadsheet row is one AWB with its duty amounts split across columns; we turn each
    // non-zero charge into its own duty line, then validate every AWB against orders.
    const CHARGE_COLS = [
        { label: 'Custom Duty', keys: ['custom duty amount', 'customs duty amount', 'custom duty', 'customs duty'] },
        { label: 'VAT', keys: ['vat amount', 'vat'] },
        { label: 'Duty Advancement Fee', keys: ['duty advancement fee', 'duty advance fee', 'advancement fee'] },
        { label: 'VAT on Duty Advancement Fee', keys: ['vat on duty advancement fee', 'vat on advancement fee'] },
        { label: 'Other Charges', keys: ['other charges', 'other charge', 'others'] },
    ];
    const AWB_KEYS = ['airway bill', 'awb', 'awb number', 'lm hawb reference', 'hawb', 'waybill'];
    const norm = (s) => String(s).toLowerCase().replace(/[#().]/g, '').replace(/\s+/g, ' ').trim();
    const pickCol = (row, keys) => {
        const want = keys.map(norm);
        for (const k of Object.keys(row)) {
            if (want.includes(norm(k))) return row[k];
        }
        return undefined;
    };
    const toNum = (v) => {
        if (v === undefined || v === null || v === '') return 0;
        const n = Number(String(v).replace(/[,\s]/g, ''));
        return isNaN(n) ? 0 : n;
    };
    let multiKey = 0;
    const onMultiFile = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setMultiFileName(file.name);
        const reader = new FileReader();
        reader.onload = async (ev) => {
            try {
                const wb = XLSX.read(ev.target.result, { type: 'array' });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                const groups = [];
                for (const r of json) {
                    const awb = String(pickCol(r, AWB_KEYS) ?? '').trim();
                    if (!awb) continue;
                    const lines = [];
                    for (const c of CHARGE_COLS) {
                        const amt = toNum(pickCol(r, c.keys));
                        if (amt > 0) lines.push({ key: ++multiKey, name: c.label, amount: amt, currency: 'AED' });
                    }
                    if (lines.length === 0) continue;
                    // merge duplicate AWB rows into one group
                    const existing = groups.find(g => g.awb === awb);
                    if (existing) existing.lines.push(...lines);
                    else groups.push({ awb, matched: null, jobId: null, lines });
                }
                if (groups.length === 0) {
                    toast.error('No AWBs with duty amounts found. Expected an "Airway Bill #" column and charge columns (Custom Duty, VAT, …).');
                    setMultiGroups([]);
                    return;
                }
                setMultiGroups(groups);
                runMatch(groups);
            } catch (err) {
                console.error(err);
                toast.error('Could not read that file. Use .xlsx, .xls or .csv.');
            }
        };
        reader.readAsArrayBuffer(file);
    };
    const runMatch = async (groups) => {
        setMatching(true);
        try {
            const res = await axios.post(dutiesUrl('match'),
                { awbs: groups.map(g => g.awb) },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
            const map = {};
            ((res.data && res.data.data) || []).forEach(m => { map[m.awb] = m; });
            setMultiGroups(prev => prev.map(g => ({
                ...g,
                matched: map[g.awb] ? map[g.awb].matched : false,
                jobId: map[g.awb] ? map[g.awb].jobId : null,
            })));
        } catch (err) {
            console.error(err);
            toast.error('AWB validation failed — you can still review and save.');
            setMultiGroups(prev => prev.map(g => ({ ...g, matched: false })));
        } finally { setMatching(false); }
    };
    const editMultiLine = (awb, key, field, value) =>
        setMultiGroups(prev => prev.map(g => g.awb !== awb ? g : {
            ...g, lines: g.lines.map(l => l.key === key ? { ...l, [field]: field === 'amount' ? value : value } : l),
        }));
    const removeMultiLine = (awb, key) =>
        setMultiGroups(prev => prev.map(g => g.awb !== awb ? g : { ...g, lines: g.lines.filter(l => l.key !== key) })
            .filter(g => g.lines.length > 0));
    const addMultiLine = (awb) =>
        setMultiGroups(prev => prev.map(g => g.awb !== awb ? g : { ...g, lines: [...g.lines, { key: ++multiKey, name: '', amount: '', currency: 'AED' }] }));
    const removeMultiGroup = (awb) => setMultiGroups(prev => prev.filter(g => g.awb !== awb));

    const multiValidGroups = useMemo(() => multiGroups
        .map(g => ({ ...g, lines: g.lines.filter(l => String(l.name).trim() && l.amount !== '' && !isNaN(Number(l.amount)) && Number(l.amount) > 0) }))
        .filter(g => g.lines.length > 0), [multiGroups]);
    const multiStats = useMemo(() => {
        const lineCount = multiValidGroups.reduce((s, g) => s + g.lines.length, 0);
        const total = multiValidGroups.reduce((s, g) => s + g.lines.reduce((t, l) => t + Number(l.amount || 0), 0), 0);
        const unmatched = multiGroups.filter(g => g.matched === false).length;
        return { awbs: multiValidGroups.length, lineCount, total, unmatched };
    }, [multiValidGroups, multiGroups]);

    const openMulti = () => { setMultiGroups([]); setMultiFileName(''); setMultiOpen(true); };
    const submitMulti = async () => {
        if (multiValidGroups.length === 0) return toast.error('Nothing valid to import.');
        setSavingMulti(true);
        try {
            const res = await axios.post(dutiesUrl('bulk-multi'),
                {
                    uploadedByEmail: email,
                    groups: multiValidGroups.map(g => ({
                        awb: g.awb,
                        items: g.lines.map(l => ({ name: String(l.name).trim(), amount: Number(l.amount), currency: l.currency || 'AED' })),
                    })),
                },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
            if (res.data && res.data.success) {
                const created = res.data.data || [];
                toast.success(`Imported ${created.length} line(s) across ${multiValidGroups.length} AWB(s).`);
                setMultiOpen(false);
                setJustAdded(created);
                loadLogs();
            } else {
                toast.error((res.data && res.data.errors && res.data.errors.map(e => e.errorMessage || e).join(', ')) || 'Import failed.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Import failed.');
        } finally { setSavingMulti(false); }
    };
    const downloadCustomsTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([{
            'Airway Bill #': '310101247596', 'Custom Duty Amount': 73, 'VAT Amount': 77.11,
            'Duty Advancement Fee': 28.57, 'VAT on Duty Advancement Fee': 1.43, 'Other Charges': 0,
        }]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'CustomsReport');
        XLSX.writeFile(wb, 'duties-tax-customs-template.xlsx');
    };

    const downloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([
            { Name: 'Customs Duty', Amount: 120.5, Currency: 'AED' },
            { Name: 'VAT', Amount: 45, Currency: 'AED' },
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'DutiesTax');
        XLSX.writeFile(wb, 'duties-tax-template.xlsx');
    };

    // ── Recent uploads log ─────────────────────────────────────────────────────
    const loadLogs = async () => {
        setLogsLoading(true);
        try {
            const res = await axios.get(dutiesUrl('logs'), { headers: authHeaders() });
            setLogs((res.data && res.data.data) || []);
        } catch (err) { console.error(err); toast.error('Failed to load upload logs.'); }
        finally { setLogsLoading(false); }
    };
    useEffect(() => { loadLogs(); /* eslint-disable-next-line */ }, []);

    // ── Add duty: build a list of charges for one AWB, then submit them together ─
    const lineIsValid = (l) => l.name.trim() && l.amount !== '' && !isNaN(Number(l.amount)) && Number(l.amount) > 0;

    const addDraftLine = () => {
        if (!lineIsValid(line)) return toast.error('Enter a charge name and a valid amount.');
        setDraft(prev => [...prev, {
            key: draftKey,
            name: line.name.trim(),
            amount: Number(line.amount),
            currency: line.currency || 'AED',
            status: line.status,
        }]);
        setDraftKey(k => k + 1);
        setLine({ name: '', amount: '', currency: 'AED', status: line.status }); // keep AED + last status
    };
    const removeDraftLine = (key) => setDraft(prev => prev.filter(d => d.key !== key));
    const draftTotal = useMemo(() => draft.reduce((s, d) => s + (Number(d.amount) || 0), 0), [draft]);

    const submitDraft = async () => {
        const target = asgAwb.trim();
        if (!target) return toast.error('Enter the AWB first.');
        // fold a half-typed charge still in the entry row into the batch too
        let lines = draft;
        if (lineIsValid(line)) {
            lines = [...draft, { key: draftKey, name: line.name.trim(), amount: Number(line.amount), currency: line.currency || 'AED', status: line.status }];
        }
        if (lines.length === 0) return toast.error('Add at least one charge first.');
        setAssigning(true);
        try {
            const res = await axios.post(dutiesUrl('assign'),
                {
                    awbs: [target],
                    uploadedByEmail: email,
                    items: lines.map(d => ({ name: d.name, amount: d.amount, currency: d.currency || 'AED', status: d.status })),
                },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
            if (res.data && res.data.success) {
                const created = res.data.data || [];
                toast.success(`Saved ${created.length} charge(s) to ${target}.`);
                setJustAdded(created);
                setDraft([]);
                setLine({ name: '', amount: '', currency: 'AED', status: 'NOT_COLLECTED' });
                loadLogs();
                await loadDuties(target);
            } else {
                toast.error((res.data && res.data.errors && res.data.errors.map(x => x.errorMessage || x).join(', ')) || 'Failed to save.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to save charges.');
        } finally { setAssigning(false); }
    };

    const total = useMemo(() => items.reduce((s, i) => s + (Number(i.amount) || 0), 0), [items]);
    const collectedCount = useMemo(() => items.filter(i => i.status === 'COLLECTED').length, [items]);
    const currency = items[0] ? (items[0].currency || 'AED') : 'AED';

    // Compact "who / when / how" cell shared by the lines table and the uploads log.
    const AddedCell = ({ byEmail, when, source }) => (
        <div>
            <div style={{ fontSize: 13, color: '#374151' }}>{byEmail || <span style={{ color: '#adb5bd' }}>System</span>}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <span style={{ fontSize: 12, color: '#9aa1ac' }} title={formatDateTime(when)}>{when ? timeAgo(when) : '—'}</span>
                <Pill tone={source === 'EXCEL' ? 'primary' : 'neutral'} icon={source === 'EXCEL' ? 'fa-file-excel-o' : 'fa-pencil'}>
                    {source === 'EXCEL' ? 'Excel' : 'Manual'}
                </Pill>
            </div>
        </div>
    );

    const StatusPill = ({ status }) => (
        <Pill tone={status === 'COLLECTED' ? 'success' : 'warning'} icon={status === 'COLLECTED' ? 'fa-check' : 'fa-clock-o'}>
            {status === 'COLLECTED' ? 'Collected' : 'Pending'}
        </Pill>
    );

    return (
        <div className="wallet-page">
            <WalletToastStack toasts={toasts} onDismiss={dismiss} />

            <div className="w-topbar">
                <div className="w-topbar-left">
                    <div className="w-icon-badge"><i className="fa fa-file-text-o" aria-hidden="true" /></div>
                    <div>
                        <h2 className="w-title">Duties &amp; Tax</h2>
                        <p className="w-subtitle">
                            Attach duty / tax lines to an Air Waybill — these show in the driver app on the collect-cash screen.
                        </p>
                    </div>
                </div>
            </div>

            {/* AWB lookup + import, one place */}
            <div className="w-toolbar">
                <span className="w-toolbar-label"><i className="fa fa-barcode" aria-hidden="true" /> AWB</span>
                <div className="w-input-wrap">
                    <i className="fa fa-search" aria-hidden="true" />
                    <input type="text" placeholder="e.g. UB04-26A6071_1" value={awb}
                        onChange={e => setAwb(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') loadDuties(); }} />
                </div>
                <button className="w-btn w-btn-primary w-btn-sm" onClick={() => loadDuties()} disabled={loading || !awb.trim()}>
                    {loading ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Loading…</> : <><i className="fa fa-search" aria-hidden="true" /> Load</>}
                </button>
                <button className="w-btn w-btn-ghost w-btn-sm" onClick={openImport} disabled={!awb.trim() && !loadedAwb}>
                    <i className="fa fa-file-excel-o" aria-hidden="true" /> Import from Excel
                </button>
                <button className="w-btn w-btn-primary w-btn-sm" onClick={openMulti}>
                    <i className="fa fa-upload" aria-hidden="true" /> Bulk import (customs report)
                </button>
            </div>

            {loadedAwb && (
                <>
                    <div className="w-stat-grid">
                        <StatCard icon="fa-list" label="Lines" value={formatInt(items.length)} />
                        <StatCard icon="fa-money" label="Total" value={`${total.toFixed(2)} ${currency}`} accent />
                        <StatCard icon="fa-check-circle" label="Collected" value={`${formatInt(collectedCount)} / ${formatInt(items.length)}`}
                            tone={collectedCount === items.length && items.length ? 'success' : 'neutral'} />
                    </div>

                    {/* Existing lines */}
                    <div className="w-card">
                        <div className="w-card-header">
                            <span className="w-card-title"><i className="fa fa-list" aria-hidden="true" /> Lines for {loadedAwb}</span>
                            <span className="w-card-meta">{formatInt(items.length)} item(s)</span>
                        </div>
                        <div className="w-card-body">
                            {loading ? <WalletSpinner label="Loading…" /> : items.length === 0 ? (
                                <EmptyState icon="fa-inbox" title="No duties & tax yet for this AWB" subtitle="Add one below or import from Excel." />
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="table table-sm align-middle" style={{ marginTop: 4, marginBottom: 0 }}>
                                        <thead><tr>
                                            <th>Name</th><th className="text-end">Amount</th><th>Status</th>
                                            <th>Added</th><th></th>
                                        </tr></thead>
                                        <tbody>
                                            {items.map(it => (
                                                <tr key={it.id}>
                                                    <td style={{ fontWeight: 600 }}>{it.name}</td>
                                                    <td className="text-end">{formatMoney(it.amount, it.currency)}</td>
                                                    <td><StatusPill status={it.status} /></td>
                                                    <td><AddedCell byEmail={it.uploadedByEmail} when={it.createdAt} source={it.source} /></td>
                                                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                                                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                            {it.status !== 'COLLECTED' && (
                                                                <IconButton icon="fa-check" tone="success" title="Mark collected" onClick={() => markCollected(it.id)} />
                                                            )}
                                                            <IconButton icon="fa-trash" tone="danger" title="Delete line" onClick={() => deleteDuty(it.id)} />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td style={{ fontWeight: 700 }}>Total</td>
                                                <td className="text-end" style={{ fontWeight: 700 }}>{formatMoney(total, currency)}</td>
                                                <td colSpan={3} />
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Add duty / tax — one AWB, add charges one by one, then submit them all */}
            <div className="w-card">
                <div className="w-card-header">
                    <span className="w-card-title"><i className="fa fa-plus-circle" aria-hidden="true" /> Add duty / tax</span>
                    <span className="w-card-meta">One AWB can have multiple charges</span>
                </div>
                <div className="w-card-body">
                    <div className="form-group" style={{ maxWidth: 360, marginBottom: 16 }}>
                        <label>Airway bill (AWB)</label>
                        <input type="text" placeholder="e.g. UB04-26A6071_1" value={asgAwb}
                            onChange={e => setAsgAwb(e.target.value)} />
                        {loadedAwb && loadedAwb !== asgAwb && (
                            <button type="button" className="w-link-btn" style={{ marginTop: 6 }} onClick={() => setAsgAwb(loadedAwb)}>
                                <i className="fa fa-plus" aria-hidden="true" /> Use {loadedAwb}
                            </button>
                        )}
                    </div>

                    {/* charge entry row */}
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div className="form-group" style={{ flex: '2 1 200px', margin: 0 }}>
                            <label>Name</label>
                            <input type="text" placeholder="e.g. Customs Duty, VAT" value={line.name}
                                onChange={e => setLine({ ...line, name: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDraftLine(); } }} />
                        </div>
                        <div className="form-group" style={{ flex: '1 1 110px', margin: 0 }}>
                            <label>Amount</label>
                            <input type="number" step="0.01" placeholder="0.00" value={line.amount}
                                onChange={e => setLine({ ...line, amount: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDraftLine(); } }} />
                        </div>
                        <div className="form-group" style={{ flex: '0 1 100px', margin: 0 }}>
                            <label>Currency</label>
                            <select value={line.currency} onChange={e => setLine({ ...line, currency: e.target.value })}>
                                <option value="AED">AED</option>
                            </select>
                        </div>
                        <div className="form-group" style={{ flex: '0 1 160px', margin: 0 }}>
                            <label>Status</label>
                            <select value={line.status} onChange={e => setLine({ ...line, status: e.target.value })}>
                                <option value="NOT_COLLECTED">Not collected</option>
                                <option value="COLLECTED">Collected</option>
                            </select>
                        </div>
                        <button type="button" className="w-btn w-btn-ghost" onClick={addDraftLine}>
                            <i className="fa fa-plus" aria-hidden="true" /> Add line
                        </button>
                    </div>

                    {/* draft charges accumulated so far */}
                    {draft.length > 0 && (
                        <div style={{ overflowX: 'auto', marginTop: 16 }}>
                            <table className="table table-sm align-middle" style={{ marginBottom: 0 }}>
                                <thead><tr><th>#</th><th>Name</th><th className="text-end">Amount</th><th>Status</th><th></th></tr></thead>
                                <tbody>
                                    {draft.map((d, i) => (
                                        <tr key={d.key}>
                                            <td style={{ color: '#9aa1ac' }}>{i + 1}</td>
                                            <td style={{ fontWeight: 600 }}>{d.name}</td>
                                            <td className="text-end">{formatMoney(d.amount, d.currency)}</td>
                                            <td><StatusPill status={d.status} /></td>
                                            <td className="text-end"><IconButton icon="fa-times" tone="danger" title="Remove" onClick={() => removeDraftLine(d.key)} /></td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td /><td style={{ fontWeight: 700 }}>Total</td>
                                        <td className="text-end" style={{ fontWeight: 700 }}>{formatMoney(draftTotal, 'AED')}</td>
                                        <td colSpan={2} />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button type="button" className="w-btn w-btn-primary" onClick={submitDraft} disabled={assigning || !asgAwb.trim()}>
                            {assigning
                                ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Submitting…</>
                                : <><i className="fa fa-check" aria-hidden="true" /> Submit{draft.length ? ` ${draft.length} charge(s)` : ''}</>}
                        </button>
                        {draft.length > 0 && (
                            <button type="button" className="w-btn w-btn-ghost" onClick={() => setDraft([])} disabled={assigning}>Clear all</button>
                        )}
                        <span className="w-help-text">Add each charge with “Add line”, then Submit to save them all to the AWB.</span>
                    </div>
                </div>
            </div>

            {/* Just added — shows the lines you just created, immediately */}
            {justAdded.length > 0 && (
                <div className="w-card" style={{ borderTop: '3px solid #16a34a' }}>
                    <div className="w-card-header">
                        <span className="w-card-title" style={{ color: '#16a34a' }}><i className="fa fa-check-circle" aria-hidden="true" /> Just added — {formatInt(justAdded.length)} line(s)</span>
                        <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => setJustAdded([])}>
                            <i className="fa fa-times" aria-hidden="true" /> Clear
                        </button>
                    </div>
                    <div className="w-card-body">
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table table-sm align-middle" style={{ marginTop: 4, marginBottom: 0 }}>
                                <thead><tr><th>AWB</th><th>Name</th><th className="text-end">Amount</th><th>Status</th><th>Source</th></tr></thead>
                                <tbody>
                                    {justAdded.map(l => (
                                        <tr key={l.id}>
                                            <td style={{ fontWeight: 600, cursor: 'pointer', color: '#0369a1' }} onClick={() => { setAwb(l.awb); loadDuties(l.awb); }}>{l.awb}</td>
                                            <td>{l.name}</td>
                                            <td className="text-end">{formatMoney(l.amount, l.currency)}</td>
                                            <td><StatusPill status={l.status} /></td>
                                            <td><Pill tone={l.source === 'EXCEL' ? 'primary' : 'neutral'} icon={l.source === 'EXCEL' ? 'fa-file-excel-o' : 'fa-pencil'}>{l.source === 'EXCEL' ? 'Excel' : 'Manual'}</Pill></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Recent uploads log — always visible */}
            <div className="w-card">
                <div className="w-card-header">
                    <span className="w-card-title"><i className="fa fa-history" aria-hidden="true" /> Upload logs — who added what &amp; when</span>
                    <button className="w-btn w-btn-ghost w-btn-sm" onClick={loadLogs} disabled={logsLoading}>
                        <i className={`fa fa-refresh ${logsLoading ? 'fa-spin' : ''}`} aria-hidden="true" /> Refresh
                    </button>
                </div>
                <div className="w-card-body">
                    {logsLoading ? <WalletSpinner label="Loading…" /> : logs.length === 0 ? (
                        <EmptyState icon="fa-history" title="No uploads yet" subtitle="Duty/tax lines you add will be logged here." />
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table table-sm align-middle" style={{ marginTop: 4, marginBottom: 0 }}>
                                <thead><tr><th>AWB</th><th>Name</th><th className="text-end">Amount</th><th>Status</th><th>Added</th></tr></thead>
                                <tbody>
                                    {logs.map(l => (
                                        <tr key={l.id}>
                                            <td style={{ fontWeight: 600, cursor: 'pointer', color: '#0369a1' }} onClick={() => { setAwb(l.awb); loadDuties(l.awb); }}>{l.awb}</td>
                                            <td>{l.name}</td>
                                            <td className="text-end">{formatMoney(l.amount, l.currency)}</td>
                                            <td><StatusPill status={l.status} /></td>
                                            <td><AddedCell byEmail={l.uploadedByEmail} when={l.createdAt} source={l.source} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Multi-AWB customs-report import modal */}
            {multiOpen && (
                <div className="modal-overlay w-modal">
                    <div className="modal-content" style={{ maxWidth: 900 }}>
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className="fa fa-file-text-o" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}>
                                <h3>Bulk import duties &amp; tax</h3>
                                <p>Upload a customs report (.xlsx/.xls/.csv). One row per AWB — each non-zero charge becomes a duty line. Review &amp; edit, then Save.</p>
                            </div>
                            <button className="close-btn" onClick={() => setMultiOpen(false)}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body">
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                                <label className="w-btn w-btn-ghost w-btn-sm w-file-btn">
                                    <i className="fa fa-upload" aria-hidden="true" /> Choose file
                                    <input type="file" accept=".xlsx,.xls,.csv" onChange={onMultiFile} style={{ display: 'none' }} />
                                </label>
                                <span className="w-file-name">{multiFileName || 'No file selected'}</span>
                                {matching && <span style={{ fontSize: 13, color: '#6b7280' }}><i className="fa fa-spinner fa-spin" /> Matching AWBs…</span>}
                                <button type="button" className="w-link-btn" style={{ marginLeft: 'auto' }} onClick={downloadCustomsTemplate}>
                                    <i className="fa fa-download" aria-hidden="true" /> Download template
                                </button>
                            </div>

                            {multiGroups.length > 0 && (
                                <>
                                    <div className="w-stat-grid" style={{ marginBottom: 12 }}>
                                        <StatCard icon="fa-barcode" label="AWBs" value={formatInt(multiStats.awbs)} />
                                        <StatCard icon="fa-list" label="Duty lines" value={formatInt(multiStats.lineCount)} />
                                        <StatCard icon="fa-money" label="Total" value={`${multiStats.total.toFixed(2)} AED`} accent />
                                        <StatCard icon="fa-exclamation-triangle" label="Unmatched" value={formatInt(multiStats.unmatched)}
                                            tone={multiStats.unmatched ? 'warning' : 'success'} />
                                    </div>
                                    {multiStats.unmatched > 0 && (
                                        <div className="w-help-text" style={{ color: '#b45309', marginBottom: 8 }}>
                                            <i className="fa fa-exclamation-triangle" aria-hidden="true" /> {multiStats.unmatched} AWB(s) don’t match any order — a driver won’t see those until an order carries that AWB. You can still save them.
                                        </div>
                                    )}
                                    <div style={{ maxHeight: 380, overflowY: 'auto', marginTop: 4 }}>
                                        {multiGroups.map(g => {
                                            const gTotal = g.lines.reduce((s, l) => s + Number(l.amount || 0), 0);
                                            return (
                                                <div key={g.awb} className="w-card" style={{ marginBottom: 10 }}>
                                                    <div className="w-card-header" style={{ paddingTop: 10, paddingBottom: 10 }}>
                                                        <span className="w-card-title" style={{ fontSize: 14 }}>
                                                            <i className="fa fa-barcode" aria-hidden="true" /> {g.awb}
                                                            {g.matched === true && <Pill tone="success" icon="fa-check">matched{g.jobId && g.jobId !== g.awb ? ` → ${g.jobId}` : ''}</Pill>}
                                                            {g.matched === false && <Pill tone="warning" icon="fa-exclamation-triangle">no order found</Pill>}
                                                            {g.matched === null && <Pill tone="neutral" icon="fa-clock-o">checking…</Pill>}
                                                        </span>
                                                        <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                            <strong style={{ fontSize: 13 }}>{gTotal.toFixed(2)} AED</strong>
                                                            <IconButton icon="fa-trash" tone="danger" title="Remove AWB" onClick={() => removeMultiGroup(g.awb)} />
                                                        </span>
                                                    </div>
                                                    <div className="w-card-body" style={{ paddingTop: 6 }}>
                                                        <table className="table table-sm align-middle" style={{ marginBottom: 6 }}>
                                                            <thead><tr><th style={{ width: '55%' }}>Charge</th><th className="text-end" style={{ width: '30%' }}>Amount (AED)</th><th></th></tr></thead>
                                                            <tbody>
                                                                {g.lines.map(l => (
                                                                    <tr key={l.key}>
                                                                        <td><input type="text" value={l.name} placeholder="Charge name"
                                                                            onChange={e => editMultiLine(g.awb, l.key, 'name', e.target.value)}
                                                                            style={{ width: '100%' }} /></td>
                                                                        <td className="text-end"><input type="number" step="0.01" value={l.amount}
                                                                            onChange={e => editMultiLine(g.awb, l.key, 'amount', e.target.value)}
                                                                            style={{ width: '100%', textAlign: 'right' }} /></td>
                                                                        <td className="text-end"><IconButton icon="fa-times" tone="danger" title="Remove line" onClick={() => removeMultiLine(g.awb, l.key)} /></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        <button type="button" className="w-link-btn" onClick={() => addMultiLine(g.awb)}>
                                                            <i className="fa fa-plus" aria-hidden="true" /> Add line
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={() => setMultiOpen(false)} disabled={savingMulti}>Cancel</button>
                            <button className="w-btn w-btn-primary" onClick={submitMulti} disabled={savingMulti || multiValidGroups.length === 0}>
                                {savingMulti ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Saving…</>
                                    : <><i className="fa fa-check" aria-hidden="true" /> Save {formatInt(multiStats.lineCount)} line(s) · {formatInt(multiStats.awbs)} AWB(s)</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Excel import modal */}
            {impOpen && (
                <div className="modal-overlay w-modal">
                    <div className="modal-content" style={{ maxWidth: 640 }}>
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className="fa fa-file-excel-o" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}>
                                <h3>Import duties &amp; tax</h3>
                                <p>For AWB <strong>{loadedAwb || awb}</strong> — columns: Name, Amount, Currency</p>
                            </div>
                            <button className="close-btn" onClick={() => setImpOpen(false)}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body">
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                                <label className="w-btn w-btn-ghost w-btn-sm w-file-btn">
                                    <i className="fa fa-upload" aria-hidden="true" /> Choose file
                                    <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} style={{ display: 'none' }} />
                                </label>
                                <span className="w-file-name">{impFileName || 'No file selected'}</span>
                                <button type="button" className="w-link-btn" style={{ marginLeft: 'auto' }} onClick={downloadTemplate}>
                                    <i className="fa fa-download" aria-hidden="true" /> Download template
                                </button>
                            </div>
                            {impRows.length > 0 && (
                                <>
                                    <div className="w-summary-line">
                                        <span>Preview — {impFileName}</span>
                                        <strong>{validImpRows.length} valid / {impRows.length} rows</strong>
                                    </div>
                                    <div style={{ maxHeight: 280, overflowY: 'auto', marginTop: 8 }}>
                                        <table className="table table-sm align-middle" style={{ marginBottom: 0 }}>
                                            <thead><tr><th>#</th><th>Name</th><th className="text-end">Amount</th><th>Currency</th><th></th></tr></thead>
                                            <tbody>
                                                {impRows.map((r, i) => {
                                                    const ok = r.name && r.amount !== '' && !isNaN(Number(r.amount));
                                                    return (
                                                        <tr key={i} style={{ opacity: ok ? 1 : 0.5 }}>
                                                            <td>{i + 1}</td>
                                                            <td>{r.name || <em style={{ color: '#e11d48' }}>missing</em>}</td>
                                                            <td className="text-end">{r.amount === '' || isNaN(Number(r.amount)) ? <em style={{ color: '#e11d48' }}>invalid</em> : Number(r.amount).toFixed(2)}</td>
                                                            <td>{r.currency || 'AED'}</td>
                                                            <td>{ok ? <i className="fa fa-check" style={{ color: '#16a34a' }} /> : <i className="fa fa-exclamation-triangle" style={{ color: '#e11d48' }} />}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={() => setImpOpen(false)} disabled={importing}>Cancel</button>
                            <button className="w-btn w-btn-primary" onClick={submitImport} disabled={importing || validImpRows.length === 0}>
                                {importing ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Importing…</> : <><i className="fa fa-upload" aria-hidden="true" /> Import {validImpRows.length} line(s)</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
